import { useCallback, useEffect, useState } from "react";
import { fetchFastApi } from "../../../api/fastapi";
import { useParams } from "react-router-dom";
import Lottie from "react-lottie";
import { Input } from "../../../components/ui/input.tsx";
import { Button } from "../../../components/ui/button.tsx";

const LoadingAnimation = () => {
  const [animationData, setAnimationData] = useState(null);

  useEffect(() => {
    fetch("/space-rocket-launch.json")
      .then((response) => response.json())
      .then((data) => setAnimationData(data));
  }, []);

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: animationData,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };
  if (!animationData) return null;

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50">
      <div className="text-center">
        <Lottie options={defaultOptions} height={200} width={200} />
        <p className="mt-4 text-xl font-bold text-white">
          Provisioning your account...
        </p>
      </div>
    </div>
  );
};

export const InviteToWorkspaceSignup = () => {
  const { inviteCode } = useParams();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [showPasswordError, setShowPasswordError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const passwordsMatch = password === confirmPassword;

  const isFormValid =
    email.trim() !== "" &&
    password.trim() !== "" &&
    confirmPassword.trim() !== "" &&
    passwordsMatch;

  useEffect(() => {
    const timer = setTimeout(() => {
      if (confirmPassword !== "" && password !== "" && !passwordsMatch) {
        setShowPasswordError(true);
      } else {
        setShowPasswordError(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [password, confirmPassword, passwordsMatch]);

  const handleEmailSubmit = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetchFastApi({
        body: {
          email,
          password,
          ...(inviteCode && { invite_code: inviteCode }),
        },
        method: "POST",
        path: "/auth/workspace/invite",
      });
      if (res.error_message) {
        setError(res.error_message);
        setIsLoading(false);
        return;
      }
      if (res.access_token) {
        localStorage.setItem("access_token", res.access_token);
        localStorage.setItem("refresh_token", res.refresh_token);
        localStorage.setItem("id_token", res.id_token);
        localStorage.setItem("email", email);
        setTimeout(() => {
          window.location.href = "/dashboard";
        }, 500);
      }
    } catch (e) {
      console.error("Error during email sign-in:", e);
      setError("An error occurred during sign-in. Please try again.");
      setIsLoading(false);
    }
  }, [password, email, inviteCode]);

  return (
    <div className="h-screen w-screen bg-[url('/gradient_bg.png')] bg-no-repeat bg-top bg-cover">
      <div className="flex h-screen justify-center items-center">
        <div className="bg-white p-8 rounded-sm shadow-2xl z-10 w-96 border border-stone-100">
          <div className="flex flex-col gap-4">
            <img src="/logo_white_bg.svg" alt="logo" className="w-20" />
            <div>
              <div className="text-2xl font-semibold">Welcome to Midship!</div>
              <div className="text-font-secondary">
                You have been invited to join a workspace on Midship. Please
                sign up below to join the workspace.
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <label htmlFor="email" className="text-sm font-semibold">
                Email
              </label>
              <Input
                type="email"
                id="email"
                placeholder="Email"
                onChange={(e) => setEmail(e.target.value)}
                value={email}
                autoComplete="email"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label htmlFor="password" className="text-sm font-semibold">
                Password
              </label>
              <Input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type="password"
                id="password"
                placeholder="Password"
                autoComplete="new-password"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label
                htmlFor="confirm-password"
                className="text-sm font-semibold"
              >
                Confirm Password
              </label>
              <Input
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                type="password"
                id="confirm-password"
                placeholder="Confirm Password"
                autoComplete="new-password"
              />
              {showPasswordError && (
                <p className="text-sm text-red-500 mt-1 transition-opacity duration-200 ease-in-out">
                  Passwords do not match
                </p>
              )}
            </div>
            <div className="text-sm text-red-500">{error}</div>
            <Button
              onClick={handleEmailSubmit}
              disabled={!isFormValid || isLoading}
            >
              {isLoading ? "Preparing launch..." : "Create account"}
            </Button>
            {inviteCode && (
              <div className="text-sm text-gray-500 text-center bg-gray-50 py-2 px-3 rounded-sm">
                Invite Code: <span className="font-medium">{inviteCode}</span>
              </div>
            )}
          </div>
        </div>
      </div>
      {isLoading && <LoadingAnimation />}
    </div>
  );
};
